import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { Pill } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { contractStatus, dateLabel, money } from '../../lib/format';
import { ContractEditor } from './ContractEditor';
import { ContractDetail } from './ContractDetail';
import type { Contract } from '../../lib/types';

export function ContractsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const canWrite = can(user, 'contracts', 'create');
  const canPrint = can(user, 'contracts', 'print');
  const [reloadSignal, setReloadSignal] = useState(0);
  // mode: 关闭 / 新建 / 编辑（携带待编辑草稿合同）
  const [editor, setEditor] = useState<{ mode: 'new' } | { mode: 'edit'; c: Contract } | null>(null);

  const refresh = () => setReloadSignal((n) => n + 1);

  const columns: Column<Contract>[] = [
    { header: '合同编号', render: (c) => <span className="num strong">{c.number}</span> },
    { header: '客户', render: (c) => c.customer_name || '—' },
    { header: '合同日期', render: (c) => <span className="num">{dateLabel(c.contract_date)}</span> },
    { header: '交付期限', render: (c) => <span className="num muted">{dateLabel(c.delivery_due_date)}</span> },
    { header: '含税总额', align: 'right', render: (c) => <span className="num strong">{money(c.grand_total, c.currency)}</span> },
    { header: '来源', render: (c) => (c.quotation_id ? <span className="muted">报价转入</span> : c.legacy_erpnext_id ? <span className="muted">ERPNext</span> : <span className="muted">手工新建</span>) },
    { header: '状态', render: (c) => { const s = contractStatus(c.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <>
      {editor ? (
        <ContractEditor
          initial={editor.mode === 'edit' ? editor.c : null}
          onCancel={() => setEditor(null)}
          onSaved={(saved) => {
            setEditor(null);
            refresh();
            // 让被保存的合同号进入列表搜索，方便定位结果。
            const url = new URL(window.location.href);
            url.searchParams.set('q', saved.number);
            window.history.replaceState({}, '', url.toString());
          }}
        />
      ) : null}

      <ListPage<Contract>
        title="销售合同"
        desc="销售合同 · /api/contracts，支持草稿新建/编辑、报价转合同快照、状态流转、交货进度与剩余可交、审计留痕，以及西语打印 / PDF（含 ERPNext 历史合同，当页展开，不用抽屉）"
        endpoint="/api/contracts"
        columns={columns}
        rowId={(c) => c.id}
        searchPlaceholder="搜索合同号 / 客户 / 状态 / 税号…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="contracts" row={row} fields={RECORD_FIELDS['contracts']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setEditor((e) => (e && e.mode === 'new' ? null : { mode: 'new' }))}>
            {editor?.mode === 'new' ? '收起新建' : '新建合同草稿'}
          </button>
        ) : undefined}
        renderDetail={(row) => (
          <ContractDetail
            row={row}
            onEdit={(c) => setEditor((e) => e ?? { mode: 'edit', c })}
            onChanged={refresh}
          />
        )}
      />
      {!canWrite ? (
        <div className="notice info" style={{ marginTop: 'var(--gap)' }}>
          当前角色为只读。销售合同的新建、编辑与状态流转由销售与管理员执行（服务端强制校验）。{canPrint ? '西语打印 / 导出 PDF 对你开放（含 ERPNext 历史合同）。' : '西语打印 / 导出 PDF 仅限管理员 / 销售 / 财务角色。'}
        </div>
      ) : null}
    </>
  );
}
